import { useEffect, useRef, useState, useCallback } from 'react';
import { Phone, X, Star, UserPlus, CalendarClock, AlertTriangle, Ban } from 'lucide-react';
import api from '../lib/api';
import { NewReservationDialog, ReservationDialog } from './ReservationDialogs';

// Polls the backend for incoming Fritz!Box calls and shows a stack of recognition
// banners (up to 3 concurrent calls) with one-tap "create reservation". The local
// bridge program feeds calls into /api/call-monitor/incoming.
const POLL_MS = 3500;

function todayStr() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function fmtUpcoming(u) {
  if (!u) return '';
  let d = u.date;
  try {
    d = new Date(u.date + 'T00:00:00').toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: 'short' });
  } catch { /* keep iso */ }
  return `${d} · ${u.start_time} · ${u.persons} Pers.${u.table_number != null ? ` · Tisch ${u.table_number}` : ''}`;
}

// Single call banner: does its own guest lookup so several can render independently.
function CallCard({ call, onDismiss, onCreate, onOpenUpcoming }) {
  const [guest, setGuest] = useState(null);
  const [blocked, setBlocked] = useState(null);

  useEffect(() => {
    let alive = true;
    setGuest(null);
    setBlocked(null);
    api.get(`/guests/lookup?phone=${encodeURIComponent(call.phone)}`)
      .then(res => { if (alive && res.data?.found) setGuest(res.data); })
      .catch(() => { /* ignore */ });
    api.get(`/blocked-guests/check?phone=${encodeURIComponent(call.phone)}`)
      .then(res => { if (alive && res.data?.blocked) setBlocked(res.data.entry); })
      .catch(() => { /* ignore */ });
    return () => { alive = false; };
  }, [call.phone]);

  return (
    <div
      data-testid="incoming-call-banner"
      className={`bg-[#1C1C19] text-white rounded-xl shadow-2xl px-4 py-3 flex items-start gap-3 border ${blocked ? 'border-[#DC2626] ring-2 ring-[#DC2626]/60' : guest?.no_shows > 0 ? 'border-[#EF4444] ring-1 ring-[#EF4444]/50' : 'border-[#3A3A35]'}`}
    >
      <div className="shrink-0 w-9 h-9 rounded-full bg-[#22C55E]/20 flex items-center justify-center mt-0.5">
        <Phone className="w-4.5 h-4.5 text-[#4ADE80] animate-pulse" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-[10px] uppercase tracking-wider text-[#A1A1AA]">Eingehender Anruf</p>
        <div className="flex items-center gap-2 flex-wrap">
          <p className="text-sm font-semibold font-mono" data-testid="incoming-call-phone">{call.phone}</p>
          {call.received_at && (
            <span className="text-[11px] text-[#A1A1AA] font-mono" data-testid="incoming-call-time">
              {new Date(call.received_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })} Uhr
            </span>
          )}
          {call.calls_today > 1 && (
            <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-[#EAB308]/20 text-[#FCD34D]" data-testid="incoming-call-count-today">
              {call.calls_today}. Anruf heute
            </span>
          )}
        </div>
        {guest && (
          <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs" data-testid="incoming-call-guest">
            <span className="font-medium text-white">{guest.name}</span>
            {guest.is_regular && (
              <span className="inline-flex items-center gap-1 text-[#FCD34D]"><Star className="w-3 h-3 fill-current" /> Stammgast</span>
            )}
            <span className="text-[#A1A1AA]">{guest.visits} Besuch{guest.visits === 1 ? '' : 'e'}</span>
            {guest.favorite_table_number != null && (
              <span className="inline-flex items-center gap-1 text-[#A1A1AA]">· <Star className="w-3 h-3" /> Tisch {guest.favorite_table_number}</span>
            )}
          </div>
        )}
        {blocked && (
          <div
            data-testid="incoming-call-blocked-warning"
            className="mt-2 flex items-start gap-2 text-xs font-medium bg-[#DC2626]/20 border border-[#DC2626] text-[#FCA5A5] rounded-md px-2 py-1.5"
          >
            <Ban className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>Gesperrter Gast{blocked.reason ? ` — ${blocked.reason}` : ''}</span>
          </div>
        )}
        {guest?.no_shows > 0 && (
          <div
            data-testid="incoming-call-noshow-warning"
            className="mt-2 flex items-center gap-2 text-xs font-medium bg-[#EF4444]/15 border border-[#EF4444]/50 text-[#FCA5A5] rounded-md px-2 py-1.5"
          >
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            <span>Achtung: {guest.no_shows} No-Show{guest.no_shows === 1 ? '' : 's'} in der Vergangenheit</span>
          </div>
        )}
        {!guest && (
          <p className="mt-0.5 text-xs text-[#A1A1AA]" data-testid="incoming-call-unknown">Neuer Gast (nicht in der Datenbank)</p>
        )}
        {guest?.upcoming && (
          <div className="mt-2 flex items-center gap-2 text-xs bg-[#22C55E]/10 border border-[#22C55E]/30 rounded-md px-2 py-1.5" data-testid="incoming-call-upcoming">
            <CalendarClock className="w-3.5 h-3.5 text-[#4ADE80] shrink-0" />
            <span className="text-[#D1FAE5] min-w-0 truncate">Reservierung: {fmtUpcoming(guest.upcoming)}</span>
          </div>
        )}
        <div className="mt-2 flex items-center gap-2 flex-wrap">
          {guest?.upcoming && (
            <button
              type="button"
              onClick={() => onOpenUpcoming(guest)}
              data-testid="incoming-call-open-reservation"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-white text-[#1C1C19] hover:bg-[#E5E5E0] transition"
            >
              <CalendarClock className="w-3.5 h-3.5" /> Reservierung öffnen
            </button>
          )}
          <button
            type="button"
            onClick={() => onCreate(call, guest)}
            data-testid="incoming-call-create"
            className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-md bg-[#22C55E] text-white hover:bg-[#16A34A] transition"
          >
            <UserPlus className="w-3.5 h-3.5" /> Reservierung anlegen
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        data-testid="incoming-call-dismiss"
        className="shrink-0 p-1 text-[#A1A1AA] hover:text-white transition"
        aria-label="Schließen"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

export default function IncomingCallBanner() {
  const [calls, setCalls] = useState([]);            // list of fresh, non-dismissed calls
  const [dialogFor, setDialogFor] = useState(null);  // { call, guest } for "create"
  const [openResv, setOpenResv] = useState(null);    // existing reservation opened from "Öffnen"
  const [tables, setTables] = useState([]);
  const [areas, setAreas] = useState([]);
  const dismissedRef = useRef(new Set());
  const metaLoadedRef = useRef(false);

  const ensureMeta = useCallback(async () => {
    if (metaLoadedRef.current) return;
    try {
      const [a, t] = await Promise.all([api.get('/areas'), api.get('/tables')]);
      setAreas(a.data || []);
      setTables(t.data || []);
      metaLoadedRef.current = true;
    } catch { /* ignore */ }
  }, []);

  // Poll for all active incoming calls (up to 3)
  useEffect(() => {
    let alive = true;
    const tick = async () => {
      try {
        const r = await api.get('/call-monitor/active');
        if (!alive) return;
        const list = (r.data?.calls || []).filter(c => !dismissedRef.current.has(c.id));
        setCalls(list);
      } catch { /* ignore (e.g. not logged in) */ }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { alive = false; clearInterval(id); };
  }, []);

  const dismiss = (id) => {
    dismissedRef.current.add(id);
    setCalls(cs => cs.filter(c => c.id !== id));
  };

  const openCreate = async (call, guest) => {
    await ensureMeta();
    setDialogFor({ call, guest });
  };

  const openUpcoming = async (guest) => {
    if (!guest?.upcoming?.id) return;
    await ensureMeta();
    try {
      const r = await api.get(`/reservations/${guest.upcoming.id}`);
      setOpenResv(r.data);
    } catch { /* ignore */ }
  };

  return (
    <>
      {calls.length > 0 && !dialogFor && (
        <div
          data-testid="incoming-call-stack"
          className="fixed z-[70] left-1/2 -translate-x-1/2 top-16 lg:top-4 w-[94vw] max-w-md space-y-2"
        >
          {calls.map(c => (
            <CallCard
              key={c.id}
              call={c}
              onDismiss={() => dismiss(c.id)}
              onCreate={openCreate}
              onOpenUpcoming={openUpcoming}
            />
          ))}
        </div>
      )}

      <NewReservationDialog
        open={!!dialogFor}
        onClose={() => setDialogFor(null)}
        tables={tables}
        areas={areas}
        defaultDate={todayStr()}
        defaultPhone={dialogFor?.call?.phone || ''}
        defaultName={dialogFor?.guest?.name || ''}
        onCreated={() => {
          if (dialogFor?.call?.id) dismiss(dialogFor.call.id);
          setDialogFor(null);
          window.dispatchEvent(new CustomEvent('reservations-changed'));
        }}
      />

      <ReservationDialog
        open={!!openResv}
        onClose={() => setOpenResv(null)}
        reservation={openResv}
        tables={tables}
        areas={areas}
        onUpdate={async () => {
          window.dispatchEvent(new CustomEvent('reservations-changed'));
          if (openResv?.id) {
            try {
              const r = await api.get(`/reservations/${openResv.id}`);
              setOpenResv(r.data);
            } catch { /* ignore */ }
          }
        }}
      />
    </>
  );
}
